import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { renameFolder as renameFolderApi, deleteFolder as deleteFolderApi, moveFolder as moveFolderApi, fetchFolders, ProviderErrorResponse, type IFolder, type IFoldersResponse } from '@apis/documents';
import { evictFolderBlobs } from './useDocumentBlob';
import { useRefreshAfterWrites } from './useRefreshAfterWrites';
import { canMoveFolder } from '../utils/validation';

// Collect a folder plus every descendant — deleting a folder removes its whole
// subtree (Genba deletes the folder and all contents).
function collectSubtree(list: IFolder[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const f of list) {
      if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) { ids.add(f.id); grew = true; }
    }
  }
  return ids;
}

// Marks every folder under a saving one: the whole branch is about to be re-keyed.
function withSavingBranches(list: IFolder[]): IFolder[] {
  const branch = new Set<string>();
  for (const f of list) if (f.saving) collectSubtree(list, f.id).forEach((id) => branch.add(id));
  return branch.size === 0 ? list : list.map((f) => branch.has(f.id) && !f.saving ? { ...f, saving: true } : f);
}

/** Stable empty tree, so memos keyed on the folders don't rerun while loading. */
const NO_FOLDERS: IFolder[] = [];

// Returns true if making `draggedId` a child of `targetId` would create a cycle.
function wouldCreateCycle(folders: IFolder[], draggedId: string, targetId: string): boolean {
  let current: string | null = targetId;
  while (current !== null) {
    if (current === draggedId) return true;
    current = folders.find((f) => f.id === current)?.parentId ?? null;
  }
  return false;
}

/**
 * The site's folder tree. The query cache is its only copy: a rename, move or
 * delete edits the cached tree at once, and puts it back if the upstream write
 * fails. Once the last write in flight settles, the tree is refetched — ids are
 * path-based, so a rename or move re-keys the folder and everything under it.
 */
export function useFolders(
  siteId: string | undefined,
  onError?: (op: 'rename' | 'delete' | 'move', error: unknown) => void,
  /** A folder was renamed or moved: its id, and everything's under it, changed. */
  onRekeyed?: (oldId: string, newId: string) => void,
) {
  const queryClient = useQueryClient();
  const queryKey = ['documents', 'folders', siteId ?? null];
  const { data, isPending, isError, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchFolders(siteId!, signal),
    enabled: !!siteId,
    // No background re-fetches — the writes below refetch when they settle.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) => (err instanceof ProviderErrorResponse ? false : failureCount < 2),
  });
  // The cached tree drives every write; what the page sees also marks the
  // branches still saving.
  const tree = data?.folders ?? NO_FOLDERS;
  const folders = useMemo(() => withSavingBranches(tree), [tree]);
  const beginWrite = useRefreshAfterWrites();

  // Edits the cached tree. A refetch in flight is stopped first: when it landed
  // it would overwrite the edit.
  async function edit(change: (list: IFolder[]) => IFolder[]) {
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData<IFoldersResponse>(queryKey, (old) => old && { ...old, folders: change(old.folders) });
  }

  // Single chokepoint for the structural rules: the server-owned roots
  // (isSystem) can't be renamed/moved/deleted, and nothing may live at the root
  // level except those roots. Every mutation refuses regardless of which UI path
  // calls it, so a stray affordance can't get around the rule.
  function isSystem(list: IFolder[], id: string): boolean {
    return list.some((f) => f.id === id && f.isSystem);
  }

  // Shows the new name at once, its branch marked as saving. The new ids arrive
  // with the refetch (which drops the mark), and only then is the rename
  // reported — whoever follows it can find the folder.
  function renameFolder(id: string, newName: string) {
    const folder = tree.find((f) => f.id === id);
    if (folder && !folder.isSystem) void persistRename(folder, newName);
  }

  // Puts a confirmed folder into the tree — it arrives with its real id already
  // set, so nothing about it is provisional. Used by useNewFolder.
  function addFolder(folder: IFolder) {
    const settled = beginWrite(queryKey);
    void edit((list) => list.some((f) => f.id === folder.id) ? list : [...list, folder]).then(settled);
  }

  async function persistRename(original: IFolder, newName: string) {
    const settled = beginWrite(queryKey);
    await edit((list) => list.map((f) => f.id === original.id ? { ...f, name: newName, saving: true } : f));
    try {
      const { folder } = await renameFolderApi(siteId!, original.id, newName);
      // The files under it are re-keyed too, so their cached bytes go as well.
      evictFolderBlobs(queryClient, siteId!, original.id);
      await settled();
      onRekeyed?.(original.id, folder.id);
      // Cached file lists under the old ids would come back stale should the
      // old path ever be reused (renamed back): drop them all, as a move does.
      void queryClient.invalidateQueries({ queryKey: ['documents', 'files', siteId] });
    } catch (err) {
      await edit((list) => list.map((f) => f.id === original.id ? original : f));
      void settled();
      onError?.('rename', err);
    }
  }

  // Removes the folder and its subtree optimistically, restoring them if the
  // upstream delete fails. Returns the in-flight delete so the confirm modal can
  // show a loading state.
  function deleteFolder(id: string): Promise<void> | void {
    if (isSystem(tree, id)) return;
    const subtree = collectSubtree(tree, id);
    return persistDelete(id, tree.filter((f) => subtree.has(f.id)));
  }

  async function persistDelete(id: string, removed: IFolder[]) {
    const settled = beginWrite(queryKey);
    const removedIds = new Set(removed.map((f) => f.id));
    await edit((list) => list.filter((f) => !removedIds.has(f.id)));
    try {
      await deleteFolderApi(siteId!, id);
      evictFolderBlobs(queryClient, siteId!, id);
    } catch (err) {
      await edit((list) => [...list, ...removed]);
      onError?.('delete', err);
    } finally {
      void settled();
    }
  }

  // Re-parents at once, marked as saving like a rename, then persists; reported
  // like a rename once the tree has the new ids. File queries under the moved subtree
  // carry stale ids once it lands, so they are invalidated wholesale.
  function moveFolder(id: string, newParentId: string | null) {
    if (newParentId === id) return;
    if (newParentId === null) return;               // can't move a folder to the root level
    if (isSystem(tree, id)) return;              // can't move a server-owned root
    if (wouldCreateCycle(tree, id, newParentId)) return;
    if (!canMoveFolder(tree, id, newParentId)) return;  // would breach the 10-level limit
    const folder = tree.find((f) => f.id === id);
    if (folder) void persistMove(folder, newParentId);
  }

  async function persistMove(original: IFolder, targetFolderId: string) {
    const settled = beginWrite(queryKey);
    await edit((list) => list.map((f) => f.id === original.id ? { ...f, parentId: targetFolderId, saving: true } : f));
    let moved: IFolder;
    try {
      ({ folder: moved } = await moveFolderApi(siteId!, original.id, targetFolderId));
      evictFolderBlobs(queryClient, siteId!, original.id);
    } catch (err) {
      await edit((list) => list.map((f) => f.id === original.id ? original : f));
      void settled();
      onError?.('move', err);
      return;
    }
    await settled();
    onRekeyed?.(original.id, moved.id);
    void queryClient.invalidateQueries({ queryKey: ['documents', 'files', siteId] });
  }

  return {
    folders,
    canDelete: data?.capabilities?.delete ?? false,
    canMove: data?.capabilities?.move ?? false,
    canApprove: data?.capabilities?.approve ?? false,
    isPending,
    isError,
    providerError: error instanceof ProviderErrorResponse ? error : null,
    renameFolder,
    deleteFolder,
    addFolder,
    moveFolder,
  };
}
